import React, { useCallback, useState } from 'react';
import { Chip, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { debounce } from 'lodash';
import { ReactComponent as DropdownArrow } from '../../assets/images/DropdownArrow.svg';
import { ReactComponent as DropdownArrowDisabled } from '../../assets/images/DropDownDisabled.svg';

/**
 * @function ControlledMultiSelect
 * @description MultiSelect component for rendering a multiple selection dropdown.
 * @param {Object} props - The props passed to the MultiSelect component.
 * @param {string} props.helperText Helper text to display below the input.
 * @param {Function} props.renderOption Function to render custom options.
 * @param {Function} props.handleBlur Function to handle the onBlur event.
 * @param {number} props.limitTags Maximum number of tags to display.
 * @param {Function} props.getTagChipLabel Function to get the label for a tag chip.
 * @param {Function} props.getOptionLabel Function to get the label for an option.
 * @param {boolean} props.disabled Flag to disable the component.
 * @param {boolean} props.disableCloseOnSelect Flag to enable closing the dropdown on option select.
 * @returns The JSX element representing the MultiSelect component.
 */
const ControlledMultiSelect = ({
  helperText,
  required = false,
  renderOption,
  handleBlur,
  limitTags = 3,
  getTagChipLabel,
  getOptionLabel,
  disabled = false,
  disableCloseOnSelect = true,
  fetchData,
  getFilteredOptions,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = React.useState([]);
  const [inputValue, setInputValue] = React.useState('');
  const [fetching, setFetching] = React.useState(false);
  const loading = open && fetching;

  const debouncedFetch = React.useMemo(
    () =>
      debounce((input) => {
        setFetching(true);
        fetchData(input)
          .then((data) => {
            const filteredList = getFilteredOptions(data);
            setOptions(filteredList);
          })
          .catch(() => {
            setOptions([]);
          })
          .finally(() => {
            setFetching(false);
          });
      }, 300),
    [fetchData]
  );

  React.useEffect(() => {
    let active = true;
    if (active && fetchData && open) {
      setFetching(true);
      fetchData(inputValue)
        .then((res) => {
          if (active) {
            const filteredList = getFilteredOptions(res);
            setOptions(filteredList);
          }
        })
        .catch(() => {
          setOptions([]);
        })
        .finally(() => {
          if (active) {
            setFetching(false);
          }
        });
    } else {
      setOptions([]);
    }

    return () => {
      active = false;
    };
  }, [fetchData, open]);
  React.useEffect(() => {
    if (!open) {
      setOptions([]);
      setFetching(false);
    }
  }, [open]);

  React.useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);
  const selectAll = {
    label: 'Select all options',
    value: 'mui_auto_complete_select_all',
  };
  const handleChange = useCallback(
    (_e, value) => {
      if (value?.includes(selectAll)) {
        if (props?.disabledOptions?.length) {
          return props?.onChange(
            props?.options?.filter(
              (ele) => !props?.disabledOptions?.includes(ele.value)
            )
          );
        }
        return props?.onChange(props?.options);
      }

      if (value?.length === 0) {
        setOpen(false);
      }
      return props?.onChange(value);
    },
    [props]
  );

  const handleAutoCompleteIsOptionEqualToValue = useCallback(
    (item, current) => item?.value === current?.value,
    []
  );

  const autoCompleteSx = useCallback(
    (theme) => ({
      '& .MuiInputBase-root.Mui-disabled': {
        background: `${theme?.palette?.other?.white1} !important`,
      },
    }),
    []
  );

  const handleDelete = (onDelete, key) => () => {
    onDelete(key);
  };

  const renderTagOption = useCallback(
    (option, index, getTagProps) => {
      const { onDelete, ...chipProps } = getTagProps({ index });

      if (option.value !== 'mui_auto_complete_select_all') {
        return (
          <Chip
            color={props?.color ? props?.color : 'primary'}
            size={props?.size ? props?.size : 'small'}
            label={getTagChipLabel ? getTagChipLabel(option) : option.label}
            onDelete={handleDelete(onDelete, chipProps?.key)}
            {...chipProps}
          />
        );
      }
      return null;
    },
    [getTagChipLabel, handleDelete, props.color, props.size]
  );

  const renderTags = (tagValue, getTagProps) =>
    tagValue?.map((option, index) =>
      renderTagOption(option, index, getTagProps)
    );

  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        name={props?.name}
        required={required}
        label={props.label}
        placeholder={!props?.value?.length ? props?.placeholder : ''}
        error={Boolean(props.error)}
        helperText={helperText}
        onBlur={handleBlur}
        InputLabelProps={{
          shrink: true,
        }}
        InputProps={{
          ...params?.InputProps,
          endAdornment: (
            <>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [props, required, helperText, handleBlur]
  );
  return (
    <Autocomplete
      disableCloseOnSelect={disableCloseOnSelect}
      size="small"
      popupIcon={disabled ? <DropdownArrowDisabled /> : <DropdownArrow />}
      isOptionEqualToValue={handleAutoCompleteIsOptionEqualToValue}
      sx={autoCompleteSx}
      renderTags={renderTags}
      renderOption={renderOption}
      renderInput={renderInput}
      {...props}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
        debouncedFetch(newInputValue);
      }}
      getOptionLabel={getOptionLabel || ((option) => option?.label)}
      multiple
      limitTags={limitTags}
      loading={loading}
      options={
        getOptionLabel || renderOption
          ? options
          : options?.length
            ? [selectAll, ...options]
            : []
      }
      onChange={handleChange}
      disabled={disabled}
      onClose={() => setOpen(false)}
      open={open}
      onOpen={() => setOpen(true)}
    />
  );
};

ControlledMultiSelect.defaultProps = {
  required: false,
  handleBlur: () => {},
  color: 'primary',
  size: 'small',
  renderOption: null,
  limitTags: 3,
  getTagChipLabel: null,
  noOptionsText: '',
  disabledOptions: [],
  getOptionLabel: null,
  helperText: '',
  placeholder: '',
  name: '',
  error: false,
  disabled: false,
  disableCloseOnSelect: true,
  open: false,
  onOpen: () => {},
  onClose: () => {},
};

ControlledMultiSelect.propTypes = {
  helperText: PropTypes.string,
  options: PropTypes.oneOfType([PropTypes.array]).isRequired,
  color: PropTypes.string,
  name: PropTypes.string,
  noOptionsText: PropTypes.string,
  size: PropTypes.string,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  renderOption: PropTypes.func,
  limitTags: PropTypes.number,
  disabledOptions: PropTypes.oneOfType([PropTypes.array]),
  getTagChipLabel: PropTypes.func,
  getOptionLabel: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.array]).isRequired,
  disabled: PropTypes.bool,
  disableCloseOnSelect: PropTypes.bool,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  open: PropTypes.bool,
  fetchData: PropTypes.func.isRequired,
  getFilteredOptions: PropTypes.func.isRequired,
};

export default ControlledMultiSelect;
